/**
 * Standing orders that come from the GROUND and the CONDITIONS rather than from the objective's rules
 * (10 §1.1: no invisible rules): a target that only opens from one face, and the campaign modifiers that
 * change how the mission is played (04 §7). `ordersLines` in missionRules.ts appends these. Pure, and driven
 * by data - the prop table, `MissionDef.modifiers` - never by which archetype this is.
 */

import { isBreached } from '../../sim/breach';
import { isDog } from '../../sim/enemies/specials/kinds';
import { PROPS, weakSideOf } from '../../sim/props';
import { DOGS } from '../../sim/tuningDogs';
import type { MissionDef, ModifierId, WeatherKind } from '../../sim/types';

/** "THE DOOR IS ON THE NORTH FACE OF EACH TARGET: A CHARGE ANYWHERE ELSE DOES NOTHING" - or null. */
export function weakSideOrders(def: MissionDef): string | null {
  const sides = new Set<string>();
  let point = 'WEAK POINT';
  let count = 0;
  for (const id of def.objective.targetPropIds) {
    const prop = def.props.find((p) => p.id === id);
    const side = prop ? weakSideOf(prop) : null;
    if (!prop || side === null) continue;
    sides.add(side.toUpperCase());
    point = PROPS[prop.kind].weakPoint ?? point;
    count++;
  }
  if (count === 0) return null;
  const where = sides.size === 1 ? `ON THE ${[...sides][0] ?? ''} FACE` : 'ON ONE FACE ONLY';
  return `THE ${point} IS ${where}${count > 1 ? ' OF EACH TARGET' : ''}: A CHARGE ANYWHERE ELSE DOES NOTHING`;
}

/**
 * What will open the targets, when a grenade will not (`PropSpec.durability`). Found on screen: a player
 * empties his grenades into the barges, the bridge piers and the relay mast before learning that those want a
 * time bomb, and that the guns and the magazines want a LAW at least. Only a mission that asks for its targets
 * DESTROYED gets the line: the hangar, the safe and the exchange are hard too, and are not to be blown up.
 */
export function durabilityOrders(def: MissionDef): string | null {
  if (def.objective.kind !== 'destroyTargets') return null;
  let bombOnly = 0;
  let lawOrBomb = 0;
  let anyBlast = 0;
  for (const id of def.objective.targetPropIds) {
    const prop = def.props.find((p) => p.id === id);
    if (!prop) continue;
    const durability = PROPS[prop.kind].durability;
    if (durability === 'bombOnly') bombOnly++;
    else if (durability === 'hard') lawOrBomb++;
    else anyBlast++;
  }
  if (bombOnly + lawOrBomb === 0) return null;
  const plural = bombOnly + lawOrBomb + anyBlast > 1;
  if (bombOnly > 0) {
    if (lawOrBomb + anyBlast > 0) return 'SOME OF THESE TARGETS OPEN ONLY TO A TIME BOMB: A GRENADE OR A LAW DOES NOTHING';
    return plural ? 'ONLY A TIME BOMB OPENS THESE TARGETS: A GRENADE OR A LAW DOES NOTHING'
      : 'ONLY A TIME BOMB OPENS THIS TARGET: A GRENADE OR A LAW DOES NOTHING';
  }
  if (anyBlast > 0) return 'SOME OF THESE TARGETS TAKE A LAW OR A TIME BOMB: A GRENADE DOES NOTHING TO THEM';
  return plural ? 'THESE TARGETS TAKE A LAW OR A TIME BOMB: A GRENADE DOES NOTHING'
    : 'THIS TARGET TAKES A LAW OR A TIME BOMB: A GRENADE DOES NOTHING';
}

/**
 * ROUND7 §4: a wall can be breached, and only by the two heaviest things he carries. A grenade against one used
 * to do nothing and say nothing; now it is refused out loud (`WALL_REFUSAL`), and the rule is said here first -
 * on every map with a wall on it, whether that is the compound's perimeter, a band's line or a cover run.
 */
export const WALLS_LINE = 'WALLS: A LAW OR A TIME BOMB BLOWS A GAP. GRENADES DO NOTHING.';

/** The walls line, or null for a map without a wall. The briefing says the same words (`briefingText`). */
export function wallsOrders(def: MissionDef): string | null {
  return def.props.some((p) => isBreached(p.kind)) ? WALLS_LINE : null;
}

/**
 * ROUND8 §4: a tracker dog on the map (02 §9.1) follows his trail, not his silhouette - the dark, the ghillie, smoke
 * and a trench do not hide him from it - and only water and the wind break it. The wind arrow under the strip map is
 * labelled with this line's last word. Keyed on the spawns, not on the Dog Patrols card, so the teams a hard mission
 * carries natively are said as surely as the card's; the trail's age is the sim's own number.
 */
export const DOGS_LINE = `DOGS FOLLOW YOUR TRAIL FOR ${DOGS.trailSeconds} S. WATER BREAKS IT. STAY DOWNWIND.`;

/**
 * The arctic's rivers are ice, and ice is not water: the trail runs straight across it (hazards/tracks.ts wipes it only
 * in water). Said, so nobody crosses a frozen river expecting the dog to lose him (integration, ROUND8).
 */
export const DOGS_LINE_ICE = `DOGS FOLLOW YOUR TRAIL FOR ${DOGS.trailSeconds} S. OPEN WATER BREAKS IT - ICE DOES NOT.`
  + ' STAY DOWNWIND.';

/** The dogs line, or null for a map without a dog. */
export function dogsOrders(def: Pick<MissionDef, 'spawns'> & Partial<Pick<MissionDef, 'biome'>>): string | null {
  if (!def.spawns.some((s) => isDog(s.kind))) return null;
  return def.biome === 'arctic' ? DOGS_LINE_ICE : DOGS_LINE;
}

/** A prisoner's start (10 §2, mission 32): the two things he cannot see from inside the cell. */
export function capturedOrders(def: MissionDef): string | null {
  if (def.startCaptured !== true) return null;
  return 'THE WIRE BEHIND THE BLOCK IS ALREADY CUT - GO THROUGH THE GAP. YOUR PODS ARE MARKED ON THE MAP';
}

/**
 * What the SKY does, for the three storms that do something (02 §5: `stormVision`, `stormFatigue`).
 *
 * The Storm card says all this on the theatre screen - but the card is not the weather. Practice never
 * deals one, and the briefing states the weather's NAME and nothing it costs, so a man stood in a
 * whiteout was told "Blizzard" and left to work out for himself that the garrison's eyes were halved.
 * Keyed off `def.weather`, not off the modifier, so a sky that blows without a card still says so.
 *
 * The thunderstorm is the one that most needed saying, because its rule REVERSES. Every other kind of
 * cover in this game stays where you left it; this one is taken back at each stroke, for everyone at
 * once, and the player it catches is exactly the one who has learnt to trust the rain.
 */
const WEATHER_ORDERS: Partial<Record<WeatherKind, string>> = {
  blizzard: 'WHITEOUT: THE SNOW HALVES WHAT THE GARRISON CAN SEE, AND RUNNING IN IT TIRES YOU FASTER',
  sandstorm: 'SANDSTORM: THE DUST HALVES WHAT THE GARRISON CAN SEE, AND RUNNING IN IT TIRES YOU FASTER',
  thunderstorm: 'THUNDERSTORM: THE RAIN HALVES WHAT THEY SEE - EVERY STROKE OF LIGHTNING GIVES IT ALL BACK FOR AN INSTANT',
};

/**
 * The line for a sky, or null for one that costs nobody anything. Keyed on the weather alone so the
 * BRIEFING can say the same thing in the same words: the orders panel has a height, and on a mission
 * carrying every modifier and a hand of intel this line is the one it drops. `withinPanel` is allowed
 * to drop a line only because "anything dropped is still in the briefing" - which was not true of this
 * one until the briefing carried it too.
 */
export function weatherLine(weather: WeatherKind): string | null {
  return WEATHER_ORDERS[weather] ?? null;
}

/** The line for this mission's sky, for the standing-orders panel. */
export function weatherOrders(def: MissionDef): string | null {
  return weatherLine(def.weather);
}

/** The modifiers that change the rules on the ground, each with the line that says how. The rest change numbers only. */
const MODIFIER_ORDERS: Partial<Record<ModifierId, string>> = {
  'scattered-drop': 'SCATTERED DROP: THE WIND CARRIES YOUR PODS FAR FROM WHERE YOU LET THEM GO',
  'hot-extraction': 'HOT EXTRACTION: WHEN THE OSPREY IS INBOUND THE ALARM GOES UP AND A SQUAD MAKES FOR THE X. IT WAITS LESS',
  'radio-silence': 'RADIO SILENCE: NO STRIP MAP AND NO MARKERS',
};

/** One line per active modifier that changes the rules, in the order the mission lists them. */
export function modifierOrders(def: MissionDef): string[] {
  const lines: string[] = [];
  for (const id of def.modifiers) {
    // No drop, no drift: a prisoner's pods already lie where they fell.
    if (id === 'scattered-drop' && def.startCaptured === true) continue;
    const text = MODIFIER_ORDERS[id];
    if (text !== undefined && !lines.includes(text)) lines.push(text);
  }
  return lines;
}

/**
 * What the intel cards did to THIS mission (04 §8). ROUND5: five of the nine promised something and no
 * part of the game read them, and the report that started this round was a player who "bought the patrol
 * routes intel but it showed nothing". A card he paid for has to say, in the orders, what it bought him.
 *
 * Only the cards that change the rules or the ground get a line. A fourth pod and a lifted storm are
 * plain on the loadout screen and in the sky; the two map cards say what they bought by being ON the map.
 * Every line is read off the `MissionDef` and not off the card list, so it states what the mission
 * actually carries - the Inside Man's guard is one the generator could, very rarely, not spare.
 */
export function intelOrders(def: MissionDef): string[] {
  const lines: string[] = [];
  const gate = def.openGate;
  if (gate !== undefined) {
    lines.push(gate.guardGone
      ? 'INSIDE MAN: ONE GATE STANDS OPEN AND ITS SENTRY IS NOT AT HIS POST. THE GATE IS THE PALE LANE ON THE MAP'
      : 'INSIDE MAN: ONE GATE STANDS OPEN - THE PALE LANE ON THE MAP. HIS SENTRY COULD NOT BE DRAWN OFF');
  }
  if (def.pickupAlt !== undefined) {
    lines.push('ALTERNATE LZ: TWO X\'S ARE OPEN. THE AIRCRAFT COMES TO WHICHEVER IS NEARER YOU WHEN IT IS CALLED');
  }
  if (def.intel.includes('jammed-radios')) {
    lines.push('JAMMED RADIOS: THE GARRISON CANNOT GET A CALL OUT - ITS FIRST WAVE OF REINFORCEMENTS IS 60 S LATE');
  }
  return lines;
}

/** Radio Silence (04 §7, 08 §2.7): the strip map is off, and the panel says why. */
export const radioSilent = (def: MissionDef): boolean => def.modifiers.includes('radio-silence');
